import { useState, useEffect } from 'react';
import type { Sponsor, Supporter } from '@/types/sponsors';
import { fetchSponsors, fetchSupporters, sortByDisplayOrder } from '@/data/sponsors-api';
import {
  createSponsor,
  updateSponsor,
  deleteSponsor,
  createSupporter,
  updateSupporter,
  deleteSupporter,
  uploadLogo,
} from '@/data/admin-api';
import { isSupabaseConfigured } from '@/lib/supabase';

const PLAN_LABELS: Record<string, string> = {
  diamond: 'ダイヤモンド',
  platinum: 'プラチナ',
  gold: 'ゴールド',
  ruby: 'ルビー',
};

export default function AdminDashboard() {
  const [tab, setTab] = useState<'sponsors' | 'supporters'>('sponsors');
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [supporters, setSupporters] = useState<Supporter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingSponsor, setEditingSponsor] = useState<Sponsor | null>(null);
  const [editingSupporter, setEditingSupporter] = useState<Supporter | null>(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [s, u] = await Promise.all([fetchSponsors(), fetchSupporters()]);
      setSponsors(s);
      setSupporters(u);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreateSponsor = async (data: Omit<Sponsor, 'id' | 'created_at'>) => {
    await createSponsor(data);
    setEditingSponsor(null);
    load();
  };

  const handleUpdateSponsor = async (id: string, data: Partial<Sponsor>) => {
    await updateSponsor(id, data);
    setEditingSponsor(null);
    load();
  };

  const handleDeleteSponsor = async (id: string) => {
    if (!confirm('削除しますか？')) return;
    await deleteSponsor(id);
    setEditingSponsor(null);
    load();
  };

  const handleCreateSupporter = async (data: Omit<Supporter, 'id' | 'created_at'>) => {
    await createSupporter(data);
    setEditingSupporter(null);
    load();
  };

  const handleUpdateSupporter = async (id: string, data: Partial<Supporter>) => {
    await updateSupporter(id, data);
    setEditingSupporter(null);
    load();
  };

  const handleDeleteSupporter = async (id: string) => {
    if (!confirm('削除しますか？')) return;
    await deleteSupporter(id);
    setEditingSupporter(null);
    load();
  };

  if (!isSupabaseConfigured()) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <h3 className="font-bold text-amber-800 mb-2">Supabaseの設定が必要です</h3>
        <p className="text-amber-700 text-sm">
          .env に VITE_SUPABASE_URL と VITE_SUPABASE_ANON_KEY を設定し、supabase/migrations のSQLを実行してください。
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-2 mb-6 border-b">
        <button
          onClick={() => setTab('sponsors')}
          className={`px-4 py-2 font-medium ${tab === 'sponsors' ? 'border-b-2 border-[#7ECEC1] text-[#7ECEC1]' : 'text-gray-600'}`}
        >
          協賛企業
        </button>
        <button
          onClick={() => setTab('supporters')}
          className={`px-4 py-2 font-medium ${tab === 'supporters' ? 'border-b-2 border-[#7ECEC1] text-[#7ECEC1]' : 'text-gray-600'}`}
        >
          後援
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-xl">{error}</div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-[#7ECEC1] border-t-transparent rounded-full" />
        </div>
      ) : tab === 'sponsors' ? (
        <SponsorsManager
          sponsors={sponsors}
          onEdit={setEditingSponsor}
          onAdd={() => setEditingSponsor({ id: '', name: '', plan: 'diamond', logo_url: null, url: null, sort_order: 0 })}
          editing={editingSponsor}
          onSave={editingSponsor?.id ? (d) => handleUpdateSponsor(editingSponsor.id, d) : handleCreateSponsor}
          onDelete={editingSponsor ? () => handleDeleteSponsor(editingSponsor.id) : undefined}
          onClose={() => { setEditingSponsor(null); setError(''); }}
          onUploadLogo={uploadLogo}
          onError={setError}
          formError={tab === 'sponsors' ? error : undefined}
        />
      ) : (
        <SupportersManager
          supporters={supporters}
          onEdit={setEditingSupporter}
          onAdd={() => setEditingSupporter({ id: '', name: '', logo_url: null, url: null, sort_order: 0 })}
          editing={editingSupporter}
          onSave={editingSupporter?.id ? (d) => handleUpdateSupporter(editingSupporter.id, d) : handleCreateSupporter}
          onDelete={editingSupporter ? () => handleDeleteSupporter(editingSupporter.id) : undefined}
          onClose={() => { setEditingSupporter(null); setError(''); }}
          onUploadLogo={uploadLogo}
          onError={setError}
          formError={tab === 'supporters' ? error : undefined}
        />
      )}
    </div>
  );
}

function SponsorsManager({
  sponsors,
  onEdit,
  onAdd,
  editing,
  onSave,
  onDelete,
  onClose,
  onUploadLogo,
  onError,
  formError,
}: {
  sponsors: Sponsor[];
  onEdit: (s: Sponsor) => void;
  onAdd: () => void;
  editing: Sponsor | null;
  onSave: (d: Partial<Sponsor>) => Promise<void>;
  onDelete?: () => void;
  onClose: () => void;
  onUploadLogo: (file: File) => Promise<string>;
  onError: (msg: string) => void;
  formError?: string;
}) {
  const [name, setName] = useState('');
  const [plan, setPlan] = useState<Sponsor['plan']>('diamond');
  const [logoUrl, setLogoUrl] = useState('');
  const [url, setUrl] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setName(editing.name);
      setPlan(editing.plan);
      setLogoUrl(editing.logo_url || '');
      setUrl(editing.url || '');
      setSortOrder(editing.sort_order ?? sponsors.length);
    }
  }, [editing, sponsors.length]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    onError('');
    try {
      let logo = logoUrl.trim() || null;
      if (logoFile) {
        logo = await onUploadLogo(logoFile);
      }
      await onSave({
        name: name?.trim() || '（名前未設定）',
        plan,
        logo_url: logo,
        url: url.trim() || null,
        sort_order: sortOrder,
      });
      setName('');
      setPlan('diamond');
      setLogoUrl('');
      setUrl('');
      setSortOrder(sponsors.length);
      setLogoFile(null);
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const grouped = { diamond: [] as Sponsor[], platinum: [] as Sponsor[], gold: [] as Sponsor[], ruby: [] as Sponsor[] };
  sponsors.forEach((s) => {
    if (grouped[s.plan]) grouped[s.plan].push(s);
  });

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={onAdd}
          className="px-4 py-2 bg-[#7ECEC1] text-white font-medium rounded-xl hover:bg-[#6ebdb0]"
        >
          ＋ 協賛を追加
        </button>
      </div>

      {(Object.keys(grouped) as (keyof typeof grouped)[]).map((p) => (
        <div key={p} className="mb-8">
          <h3 className="text-lg font-bold text-gray-800 mb-3">{PLAN_LABELS[p]}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {sortByDisplayOrder(grouped[p]).map((s) => (
              <div
                key={s.id}
                className="bg-white rounded-xl p-4 shadow-sm border flex flex-col items-center justify-center min-h-[100px] cursor-pointer hover:shadow-md"
                onClick={() => onEdit(s)}
              >
                {s.logo_url ? (
                  <img src={s.logo_url} alt={s.name} className="max-h-16 w-auto object-contain" />
                ) : (
                  <span className="text-gray-600 text-sm text-center">{s.name}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">{editing.id ? '編集' : '新規追加'}</h3>
            {formError && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-xl text-sm">{formError}</div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">企業名</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2 border rounded-xl"
                  required
                  placeholder="株式会社〇〇"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">プラン</label>
                <select
                  value={plan}
                  onChange={(e) => setPlan(e.target.value as Sponsor['plan'])}
                  className="w-full px-4 py-2 border rounded-xl"
                >
                  {Object.entries(PLAN_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">企業URL</label>
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="w-full px-4 py-2 border rounded-xl"
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">表示順</label>
                <input
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
                  className="w-full px-4 py-2 border rounded-xl"
                  min={0}
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">小さいほど先に表示。未入力時は登録順</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ロゴ画像</label>
                <p className="text-xs text-gray-500 mb-1">URLを入力するか、ファイルを選択してください</p>
                <input
                  type="text"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  className="w-full px-4 py-2 border rounded-xl mb-2"
                  placeholder="https://..."
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                  className="w-full text-sm"
                />
                {editing.logo_url && !logoFile && (
                  <div className="mt-2">
                    <img src={editing.logo_url} alt="" className="h-12 object-contain" />
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-4">
                <button type="submit" disabled={saving} className="flex-1 py-2 bg-[#7ECEC1] text-white font-medium rounded-xl disabled:opacity-50">
                  {saving ? '保存中...' : '保存'}
                </button>
                {onDelete && (
                  <button type="button" onClick={onDelete} className="px-4 py-2 bg-red-100 text-red-700 rounded-xl">
                    削除
                  </button>
                )}
                <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 rounded-xl">
                  閉じる
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function SupportersManager({
  supporters,
  onEdit,
  onAdd,
  editing,
  onSave,
  onDelete,
  onClose,
  onUploadLogo,
  onError,
  formError,
}: {
  supporters: Supporter[];
  onEdit: (s: Supporter) => void;
  onAdd: () => void;
  editing: Supporter | null;
  onSave: (d: Partial<Supporter>) => Promise<void>;
  onDelete?: () => void;
  onClose: () => void;
  onUploadLogo: (file: File) => Promise<string>;
  onError: (msg: string) => void;
  formError?: string;
}) {
  const [name, setName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [url, setUrl] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setName(editing.name);
      setLogoUrl(editing.logo_url || '');
      setUrl(editing.url || '');
      setSortOrder(editing.sort_order ?? supporters.length);
    }
  }, [editing, supporters.length]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    onError('');
    try {
      let logo = logoUrl.trim() || null;
      if (logoFile) {
        logo = await onUploadLogo(logoFile);
      }
      await onSave({
        name: name?.trim() || '（名前未設定）',
        logo_url: logo,
        url: url.trim() || null,
        sort_order: sortOrder,
      });
      setName('');
      setLogoUrl('');
      setUrl('');
      setSortOrder(supporters.length);
      setLogoFile(null);
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={onAdd}
          className="px-4 py-2 bg-[#7ECEC1] text-white font-medium rounded-xl hover:bg-[#6ebdb0]"
        >
          ＋ 後援を追加
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {sortByDisplayOrder(supporters).map((s) => (
          <div
            key={s.id}
            className="bg-white rounded-xl p-4 shadow-sm border flex flex-col items-center justify-center min-h-[80px] cursor-pointer hover:shadow-md"
            onClick={() => onEdit(s)}
          >
            {s.logo_url ? (
              <img src={s.logo_url} alt={s.name} className="max-h-12 w-auto object-contain" />
            ) : (
              <span className="text-gray-600 text-sm text-center">{s.name}</span>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">{editing.id ? '編集' : '新規追加'}</h3>
            {formError && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-xl text-sm">{formError}</div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">名称</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2 border rounded-xl"
                  required
                  placeholder="〇〇庁／〇〇協会"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="w-full px-4 py-2 border rounded-xl"
                  placeholder="https://example.jp"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">表示順</label>
                <input
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
                  className="w-full px-4 py-2 border rounded-xl"
                  min={0}
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">小さいほど先に表示。未入力時は登録順</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">画像（任意）</label>
                <p className="text-xs text-gray-500 mb-1">URLを入力するか、ファイルを選択してください</p>
                <input
                  type="text"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  className="w-full px-4 py-2 border rounded-xl mb-2"
                  placeholder="https://..."
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                  className="w-full text-sm"
                />
                {editing.logo_url && !logoFile && (
                  <div className="mt-2">
                    <img src={editing.logo_url} alt="" className="h-12 object-contain" />
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-4">
                <button type="submit" disabled={saving} className="flex-1 py-2 bg-[#7ECEC1] text-white font-medium rounded-xl disabled:opacity-50">
                  {saving ? '保存中...' : '保存'}
                </button>
                {onDelete && (
                  <button type="button" onClick={onDelete} className="px-4 py-2 bg-red-100 text-red-700 rounded-xl">
                    削除
                  </button>
                )}
                <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 rounded-xl">
                  閉じる
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
